// Search Overlay - Google Maps-like search experience

import { useState, useEffect, useRef, useCallback } from 'react';
import { useMapStore, useSearchStore } from '../../stores';
import { api } from '../../lib/api';
import { cn, getCategoryIcon, getLeadTypeColor, getLeadTypeLabel, formatDistance, calculateDistance } from '../../lib/utils';
import type { Business, Coordinates } from '../../types';
import {
  Search,
  X,
  MapPin,
  Clock,
  Star,
  Globe,
  Building2,
  ArrowUp,
  CornerDownLeft,
  Loader2,
  Navigation,
  Home,
  Briefcase,
  Utensils,
  ShoppingBag,
  Heart,
  Coffee,
  Car,
  Wrench,
  Scissors,
  Dumbbell,
  GraduationCap,
  Landmark,
} from 'lucide-react';

interface SearchOverlayProps {
  onClose: () => void;
  onSearch: (query: string) => void;
  recentLocations?: Coordinates[];
}

// Quick search categories like Google Maps
const QUICK_SEARCHES = [
  { label: 'Restaurants', icon: Utensils, query: 'restaurant' },
  { label: 'Coffee', icon: Coffee, query: 'cafe' },
  { label: 'Shopping', icon: ShoppingBag, query: 'retail' },
  { label: 'Hotels', icon: Home, query: 'hotel' },
  { label: 'Gas', icon: Car, query: 'automotive' },
  { label: 'Services', icon: Wrench, query: 'service' },
  { label: 'Health', icon: Heart, query: 'health' },
  { label: 'Beauty', icon: Scissors, query: 'beauty' },
  { label: 'Fitness', icon: Dumbbell, query: 'fitness' },
  { label: 'Education', icon: GraduationCap, query: 'education' },
  { label: 'Finance', icon: Landmark, query: 'finance' },
  { label: 'B2B', icon: Briefcase, query: 'b2b' },
];

export function SearchOverlay({ onClose, onSearch, recentLocations = [] }: SearchOverlayProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Business[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  const { setMapState, mapState, setFilters, setLeads } = useMapStore();
  const { searchHistory, addToSearchHistory, addRecentLocation } = useSearchStore();

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Debounced search
  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    const timer = setTimeout(async () => {
      try {
        const searchResults = await api.searchLeads(query, mapState.center, 25);
        setResults(searchResults);
        setActiveIndex(-1);
      } catch (error) {
        console.error('Search failed:', error);
        setResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query, mapState.center]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(prev => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(prev => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIndex >= 0 && results[activeIndex]) {
        handleSelectResult(results[activeIndex]);
      } else if (query.trim()) {
        handleSubmitSearch();
      }
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  // Submit search as filter
  const handleSubmitSearch = useCallback(() => {
    if (!query.trim()) return;
    setFilters({ keywords: [query.trim()] });
    addToSearchHistory(query.trim());
    onSearch(query.trim());
    onClose();
  }, [query, setFilters, addToSearchHistory, onSearch, onClose]);

  // Select a specific result
  const handleSelectResult = useCallback((lead: Business) => {
    setMapState({ 
      center: lead.coordinates, 
      zoom: 17,
      pitch: 0,
      bearing: 0,
    });
    addRecentLocation(lead.coordinates);
    addToSearchHistory(lead.name);
    onSearch(lead.name);
    onClose();
  }, [setMapState, addRecentLocation, addToSearchHistory, onSearch, onClose]);

  // Handle quick search
  const handleQuickSearch = useCallback((searchQuery: string) => {
    setQuery(searchQuery);
  }, []);

  // Handle "search this area"
  const handleSearchArea = useCallback(() => {
    if (query.trim()) {
      setFilters({ keywords: [query.trim()] });
      addToSearchHistory(query.trim());
      onClose();
    }
  }, [query, setFilters, addToSearchHistory, onClose]);

  // Handle current location
  const handleCurrentLocation = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setMapState({ center: coords, zoom: 16 });
          addRecentLocation(coords);
          onClose();
        }
      );
    }
  }, [setMapState, addRecentLocation, onClose]);

  return (
    <div className="fixed inset-0 z-[100] bg-black/30 backdrop-blur-sm animate-fade-in"
         onClick={onClose}>
      <div 
        className="w-full max-w-2xl mx-auto mt-20 px-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search Container */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden animate-slide-down">
          {/* Search Input */}
          <div className="flex items-center gap-3 p-4 border-b border-slate-200">
            <Search className="w-5 h-5 text-slate-400 flex-shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search for businesses, places, addresses..."
              className="flex-1 text-lg text-slate-900 placeholder-slate-400 outline-none bg-transparent"
            />
            {isSearching && (
              <Loader2 className="w-5 h-5 text-slate-400 animate-spin flex-shrink-0" />
            )}
            {query && !isSearching && (
              <button
                onClick={() => setQuery('')}
                className="p-1 rounded-full hover:bg-slate-100 text-slate-400 transition-colors"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
            <kbd className="hidden sm:flex items-center gap-1 px-2 py-1 text-xs text-slate-500 bg-slate-100 rounded border border-slate-200">
              <CornerDownLeft className="w-3 h-3" />
              Enter
            </kbd>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors lg:hidden"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Results Area */}
          <div ref={resultsRef} className="max-h-[60vh] overflow-y-auto">
            {/* No query - show quick searches and history */}
            {query.trim().length < 2 && (
              <div className="p-4 space-y-6">
                {/* Current Location */}
                <button
                  onClick={handleCurrentLocation}
                  className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors text-left"
                >
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <Navigation className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Use your current location</p>
                    <p className="text-sm text-slate-500">Find leads near you</p>
                  </div>
                </button>

                {/* Search History */}
                {searchHistory.length > 0 && (
                  <div>
                    <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 px-1">
                      Recent Searches
                    </h3>
                    <div className="space-y-1">
                      {searchHistory.slice(0, 5).map((item, i) => (
                        <button
                          key={i}
                          onClick={() => setQuery(item)}
                          className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-slate-50 transition-colors text-left group"
                        >
                          <Clock className="w-4 h-4 text-slate-400 flex-shrink-0" />
                          <span className="text-slate-700 flex-1 truncate">{item}</span>
                          <ArrowUp className="w-4 h-4 text-slate-300 opacity-0 group-hover:opacity-100 rotate-45 transition-opacity" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Quick Searches */}
                <div>
                  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 px-1">
                    Categories
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {QUICK_SEARCHES.map(item => (
                      <button
                        key={item.label}
                        onClick={() => handleQuickSearch(item.query)}
                        className="flex items-center gap-2.5 p-3 rounded-xl border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-all text-left group"
                      >
                        <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center group-hover:bg-primary-100 transition-colors flex-shrink-0">
                          <item.icon className="w-4 h-4 text-slate-600 group-hover:text-primary-600 transition-colors" />
                        </div>
                        <span className="text-sm font-medium text-slate-700 truncate">{item.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Search Results */}
            {query.trim().length >= 2 && (
              <div className="py-2">
                {/* Search this area option */}
                <button
                  onClick={handleSearchArea}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-primary-50 transition-colors text-left border-b border-slate-100"
                >
                  <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <Search className="w-4 h-4 text-primary-600" />
                  </div>
                  <div>
                    <p className="font-medium text-primary-700">Search this area</p>
                    <p className="text-xs text-slate-500">Find all matching businesses here</p>
                  </div>
                </button>

                {/* Results list */}
                {results.length > 0 ? (
                  <div className="max-h-[45vh] overflow-y-auto">
                    {results.map((lead, index) => {
                      const distance = calculateDistance(mapState.center, lead.coordinates);
                      return (
                        <button
                          key={lead.id}
                          onClick={() => handleSelectResult(lead)}
                          onMouseEnter={() => setActiveIndex(index)}
                          className={cn(
                            'w-full flex items-center gap-3 px-4 py-3 transition-colors text-left border-b border-slate-50 last:border-0',
                            activeIndex === index ? 'bg-slate-50' : 'hover:bg-slate-50'
                          )}
                        >
                          <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center text-xl flex-shrink-0">
                            {getCategoryIcon(lead.category)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-slate-900 truncate">{lead.name}</p>
                              <span className={cn('badge text-xs px-1.5 py-0 flex-shrink-0', getLeadTypeColor(lead.leadType))}>
                                {getLeadTypeLabel(lead.leadType)}
                              </span>
                            </div>
                            <p className="text-sm text-slate-500 truncate">{lead.address}</p>
                            <div className="flex items-center gap-2 mt-1 text-xs text-slate-400">
                              {lead.rating && (
                                <span className="flex items-center gap-0.5 text-amber-600">
                                  <Star className="w-3 h-3 fill-current" />
                                  {lead.rating}
                                </span>
                              )}
                              <span>•</span>
                              <span>{lead.category}</span>
                              <span>•</span>
                              <span>{formatDistance(distance)}</span>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : !isSearching ? (
                  <div className="px-4 py-8 text-center">
                    <Search className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No businesses found for "{query}"</p>
                    <button
                      onClick={handleSearchArea}
                      className="mt-3 btn-primary text-sm"
                    >
                      Search this area anyway
                    </button>
                  </div>
                ) : (
                  <div className="px-4 py-8 text-center">
                    <Loader2 className="w-8 h-8 text-slate-300 mx-auto mb-3 animate-spin" />
                    <p className="text-slate-500">Searching...</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <kbd className="px-1.5 py-0.5 bg-white rounded border border-slate-200 font-mono">↑↓</kbd>
              <span>Navigate</span>
              <kbd className="px-1.5 py-0.5 bg-white rounded border border-slate-200 font-mono ml-2">↵</kbd>
              <span>Select</span>
              <kbd className="px-1.5 py-0.5 bg-white rounded border border-slate-200 font-mono ml-2">Esc</kbd>
              <span>Close</span>
            </div>
            <button
              onClick={onClose}
              className="btn-ghost text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SearchOverlay;